/**
 * Cloud configuration discovery, parsing, and merging.
 *
 * Discovers `.testrelic/testrelic-config.json` config files, resolves environment variable
 * references, and merges configuration from multiple sources.
 *
 * Priority order (highest wins):
 *   1. Environment variables (TESTRELIC_API_KEY, TESTRELIC_CLOUD_ENDPOINT, etc.)
 *   2. Reporter options in wdio.conf.ts
 *   3. .testrelic/testrelic-config.json config file
 *   4. Built-in defaults
 */

import { readFileSync, existsSync, statSync } from 'node:fs';
import { join, dirname, resolve } from 'node:path';
import type { CloudConfig, UploadStrategy, CloudReporterOptions } from '@testrelic/core';
import { isValidCloudConfig, isValidEndpointUrl } from '@testrelic/core';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONFIG_DIR = '.testrelic';
const CONFIG_FILENAME = 'testrelic-config.json';
const LEGACY_CONFIG_FILENAME = '.testrelic';
const MAX_WALK_UP_LEVELS = 5;

const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const DEFAULT_CLOUD_CONFIG: CloudConfig = Object.freeze({
  apiKey: null,
  endpoint: 'https://platform.testrelic.ai/api/v1',
  uploadStrategy: 'batch',
  timeout: 30000,
  projectName: null,
  queueMaxAge: 604800000,
  queueDirectory: `${CONFIG_DIR}/queue`,
  uploadArtifacts: true,
  artifactMaxSizeMb: 50,
});

const DURATION_UNITS: Record<string, number> = {
  s: 1000,
  m: 60 * 1000,
  h: 60 * 60 * 1000,
  d: 24 * 60 * 60 * 1000,
};

// ---------------------------------------------------------------------------
// Config File Discovery
// ---------------------------------------------------------------------------

export function discoverConfigFile(startDir: string): string | null {
  let currentDir = resolve(startDir);
  for (let i = 0; i <= MAX_WALK_UP_LEVELS; i++) {
    const candidate = join(currentDir, CONFIG_DIR, CONFIG_FILENAME);
    if (existsSync(candidate)) {
      try {
        if (statSync(candidate).isFile()) return candidate;
      } catch {
        // ignore stat errors, continue walking
      }
    }

    const legacyCandidate = join(currentDir, LEGACY_CONFIG_FILENAME);
    if (existsSync(legacyCandidate)) {
      try {
        if (statSync(legacyCandidate).isFile()) {
          process.stderr.write(
            '[testrelic] Deprecation: config file ".testrelic" has moved to ".testrelic/testrelic-config.json". ' +
              'Please migrate your config file to the new location.\n',
          );
          return legacyCandidate;
        }
      } catch {
        // ignore stat errors
      }
    }

    const parentDir = dirname(currentDir);
    if (parentDir === currentDir) break;
    currentDir = parentDir;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Config File Parsing
// ---------------------------------------------------------------------------

export function parseConfigFile(filePath: string): Record<string, unknown> | null {
  try {
    const content = readFileSync(filePath, 'utf-8');
    const parsed: unknown = JSON.parse(content);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    if (!isSafeObject(parsed as Record<string, unknown>)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isSafeObject(obj: Record<string, unknown>): boolean {
  for (const key of Object.keys(obj)) {
    if (DANGEROUS_KEYS.has(key)) return false;
    const val = obj[key];
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      if (!isSafeObject(val as Record<string, unknown>)) return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Environment Variable Resolution
// ---------------------------------------------------------------------------

export function resolveEnvVar(value: string): string | null {
  const bracketMatch = /^\$\{([A-Za-z_][A-Za-z0-9_]*)\}$/.exec(value);
  if (bracketMatch) {
    return process.env[bracketMatch[1]] ?? null;
  }
  const dollarMatch = /^\$([A-Za-z_][A-Za-z0-9_]*)$/.exec(value);
  if (dollarMatch) {
    return process.env[dollarMatch[1]] ?? null;
  }
  return value;
}

export function resolveEnvVars(obj: Record<string, unknown>): Record<string, unknown> {
  const result = Object.create(null) as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (typeof val === 'string' && val.startsWith('$')) {
      result[key] = resolveEnvVar(val);
    } else if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      result[key] = resolveEnvVars(val as Record<string, unknown>);
    } else {
      result[key] = val;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Duration Parsing
// ---------------------------------------------------------------------------

export function parseDuration(value: string): number | null {
  const match = /^(\d+)\s*([smhd])$/.exec(value.trim());
  if (!match) return null;
  const amount = parseInt(match[1], 10);
  const unit = match[2];
  const multiplier = DURATION_UNITS[unit];
  if (!multiplier || amount <= 0) return null;
  return amount * multiplier;
}

// ---------------------------------------------------------------------------
// Config Merging
// ---------------------------------------------------------------------------

export function mergeCloudConfig(
  fileConfig: Record<string, unknown> | null,
  reporterOptions: CloudReporterOptions | undefined,
): CloudConfig {
  const target = Object.create(null) as Record<string, unknown>;

  Object.assign(target, DEFAULT_CLOUD_CONFIG);

  if (fileConfig) {
    const cloud = fileConfig.cloud as Record<string, unknown> | undefined;
    if (cloud && typeof cloud === 'object') {
      if (typeof cloud.endpoint === 'string') target.endpoint = cloud.endpoint;
      if (typeof cloud.upload === 'string') target.uploadStrategy = cloud.upload;
      if (typeof cloud.timeout === 'number') target.timeout = cloud.timeout;
      if (typeof cloud.apiKey === 'string' && cloud.apiKey.length > 0) {
        target.apiKey = cloud.apiKey;
      }
    }
    const repoSection = (fileConfig['testrelic-repo'] ?? fileConfig.project) as Record<string, unknown> | undefined;
    if (repoSection && typeof repoSection === 'object') {
      if (typeof repoSection.name === 'string') target.projectName = repoSection.name;
    }
    if (fileConfig.project && !fileConfig['testrelic-repo']) {
      process.stderr.write(
        '[testrelic] Deprecation: "project" key in config is deprecated. Rename it to "testrelic-repo".\n',
      );
    }
    const queue = fileConfig.queue as Record<string, unknown> | undefined;
    if (queue && typeof queue === 'object') {
      if (typeof queue.maxAge === 'string') {
        const ms = parseDuration(queue.maxAge as string);
        if (ms !== null) target.queueMaxAge = ms;
      }
      if (typeof queue.directory === 'string') target.queueDirectory = queue.directory;
    }
  }

  if (reporterOptions) {
    if (typeof reporterOptions.apiKey === 'string' && reporterOptions.apiKey.length > 0) {
      const resolved = reporterOptions.apiKey.startsWith('$')
        ? resolveEnvVar(reporterOptions.apiKey)
        : reporterOptions.apiKey;
      if (resolved) target.apiKey = resolved;
    }
    if (typeof reporterOptions.endpoint === 'string') {
      const resolved = reporterOptions.endpoint.startsWith('$')
        ? resolveEnvVar(reporterOptions.endpoint)
        : reporterOptions.endpoint;
      if (resolved) target.endpoint = resolved;
    }
    if (typeof reporterOptions.upload === 'string') target.uploadStrategy = reporterOptions.upload;
    if (typeof reporterOptions.timeout === 'number') target.timeout = reporterOptions.timeout;
    if (typeof reporterOptions.projectName === 'string') target.projectName = reporterOptions.projectName;
    if (typeof reporterOptions.queueMaxAge === 'string') {
      const ms = parseDuration(reporterOptions.queueMaxAge);
      if (ms !== null) target.queueMaxAge = ms;
    }
    if (typeof reporterOptions.queueDirectory === 'string') target.queueDirectory = reporterOptions.queueDirectory;
    if (typeof reporterOptions.uploadArtifacts === 'boolean') target.uploadArtifacts = reporterOptions.uploadArtifacts;
    if (typeof reporterOptions.artifactMaxSizeMb === 'number') target.artifactMaxSizeMb = reporterOptions.artifactMaxSizeMb;
  }

  const envApiKey = process.env.TESTRELIC_API_KEY;
  if (envApiKey && envApiKey.length > 0) {
    target.apiKey = envApiKey;
  }

  const envEndpoint = process.env.TESTRELIC_CLOUD_ENDPOINT;
  if (envEndpoint && isValidEndpointUrl(envEndpoint)) {
    target.endpoint = envEndpoint;
  }

  const envUpload = process.env.TESTRELIC_UPLOAD_STRATEGY;
  if (envUpload && ['batch', 'realtime', 'both'].includes(envUpload)) {
    target.uploadStrategy = envUpload as UploadStrategy;
  }

  const envTimeout = process.env.TESTRELIC_CLOUD_TIMEOUT;
  if (envTimeout) {
    const parsed = parseInt(envTimeout, 10);
    if (!isNaN(parsed) && parsed >= 1000 && parsed <= 120000) {
      target.timeout = parsed;
    }
  }

  const config = Object.freeze(target) as unknown as CloudConfig;

  if (!isValidCloudConfig(config)) {
    return DEFAULT_CLOUD_CONFIG;
  }

  return config;
}

export { DEFAULT_CLOUD_CONFIG };
